const STORAGE_KEY = "initial_utm";

const UTM_PREFIX = "utm_";

type TInitialUtmKey = `initial_utm_${string}`;

export type TUtmAttribution = Partial<Record<TInitialUtmKey, string>>;

const isInitialUtmKey = (key: string): key is TInitialUtmKey =>
  key.startsWith(`initial_${UTM_PREFIX}`);

// Every utm_* param on the URL, not just the standard five.
const readUtmFromUrl = (): TUtmAttribution => {
  const params = new URLSearchParams(window.location.search);
  const utm: TUtmAttribution = {};

  params.forEach((value, key) => {
    if (key.startsWith(UTM_PREFIX) && value) {
      utm[`initial_${key}` as TInitialUtmKey] = value;
    }
  });

  return utm;
};

const parseStoredUtm = (raw: string): TUtmAttribution => {
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed !== "object" || parsed === null) return {};

  const utm: TUtmAttribution = {};

  Object.entries(parsed).forEach(([key, value]) => {
    if (isInitialUtmKey(key) && typeof value === "string") {
      utm[key] = value;
    }
  });

  return utm;
};

/** Stores first-touch UTMs; a later campaign does not overwrite the acquiring one. */
export const captureInitialUtm = () => {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return;

    const utm = readUtmFromUrl();
    if (!Object.keys(utm).length) return;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
  } catch {
    return;
  }
};

/** Reads back the UTMs captured on the first tagged landing. */
export const getInitialUtm = (): TUtmAttribution => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseStoredUtm(stored) : {};
  } catch {
    return {};
  }
};

export type TCheckoutMetadata = Partial<
  Record<`utm_${string}` | "type", string>
>;

// Captured UTMs as bare utm_* keys plus type (mirrored from utm_source), for checkout metadata.
export const toCheckoutMetadata = (
  initial: TUtmAttribution,
): TCheckoutMetadata => {
  const metadata: TCheckoutMetadata = {};

  Object.entries(initial).forEach(([key, value]) => {
    if (value) {
      metadata[key.replace(/^initial_/, "") as `utm_${string}`] = value;
    }
  });

  if (metadata.utm_source) {
    metadata.type = metadata.utm_source;
  }

  return metadata;
};
